import {
  convertDateToUTCISOString,
  datePickerDefaultOptions,
} from '@UI/aside/asideDatePicker/dateHelper';
import AsideEditor from '@UI/editor/AsideEditor';
import SimpleRadioButton from '@UI/radioButton/SimpleRadioButton';
import type { RootState } from '@generalTypes/rootStateTypes';
// @ts-ignore shared component package has no published TS declarations
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { selectLatestSnapshot } from '@newStore/documentSnapshot/documentSnapshotHelpers';
import { stripHtml } from '@newStore/genericHelpers';
import { nlBE } from 'date-fns/locale/nl-BE';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './modals.scss';

export type KrcSnapshotMode = 'new' | 'overwrite';
export type KrcVersionType = 'minor' | 'major';

export interface KrcSnapshotFormData {
  changelog: string;
  startDate: string;
  infoUrl: string;
  versionType?: KrcVersionType;
}

interface KrcSnapshotModalProps {
  mode: KrcSnapshotMode;
  initialValues?: Partial<KrcSnapshotFormData>;
  onClose?: () => void;
  onConfirm?: (data: KrcSnapshotFormData) => void;
}

const KrcSnapshotModal: React.FC<KrcSnapshotModalProps> = ({
  mode,
  initialValues = {},
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const initialChangelog = initialValues.changelog ?? '';
  const [changelog, setChangelog] = useState(initialChangelog);
  const [startDate, setStartDate] = useState<Date | null>(
    initialValues.startDate ? new Date(initialValues.startDate) : null,
  );
  const [infoUrl, setInfoUrl] = useState(initialValues.infoUrl ?? '');
  const [versionType, setVersionType] = useState<KrcVersionType>(
    initialValues.versionType ?? 'minor',
  );
  const preparedChangelog = useSelector(
    (state: RootState) => state.documentSnapshot.snapshotChangelog,
  );
  const changelogStatus = useSelector(
    (state: RootState) => state.documentSnapshot.snapshotChangelogStatus,
  );
  const latestSnapshot = useSelector((state: RootState) =>
    selectLatestSnapshot(state.documentSnapshot.snapshots),
  );

  // A new version must start after the latest published version.
  const latestStartDate = latestSnapshot?.startDate ? new Date(latestSnapshot.startDate) : null;
  const isStartDateNotAfterLatest =
    mode === 'new' &&
    startDate !== null &&
    latestStartDate !== null &&
    convertDateToUTCISOString(startDate) <= convertDateToUTCISOString(latestStartDate);

  const isPreparingChangelog =
    mode === 'new' && changelogStatus !== 'succeeded' && changelogStatus !== 'failed';
  const hasChangelogPreparationFailed = mode === 'new' && changelogStatus === 'failed';

  useEffect(() => {
    if (mode === 'new' && changelogStatus === 'succeeded') {
      setChangelog(preparedChangelog);
    }
  }, [changelogStatus, mode, preparedChangelog]);

  const title =
    mode === 'new' ? 'Nieuwe versie publiceren' : 'Laatste gepubliceerde versie aanpassen';

  const handleConfirm = () => {
    onConfirm({
      changelog,
      startDate: startDate ? convertDateToUTCISOString(startDate) : '',
      infoUrl,
      ...(mode === 'new' && { versionType }),
    });
    onClose();
  };

  const isInfoUrlInvalid = !(infoUrl.startsWith('https://') || infoUrl.startsWith('http://'));
  const isConfirmDisabled =
    stripHtml(changelog).trim() === '' ||
    startDate === null ||
    isInfoUrlInvalid ||
    isStartDateNotAfterLatest;

  return (
    <div className="overflown-modal">
      <Modal.Header title={title} onClose={onClose} />
      <Modal.Body>
        {mode === 'new' && (
          <div className="form-group">
            <label className="control-label">Versie type</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <SimpleRadioButton
                name="versionType"
                label="Minor versie (kleine aanpassingen)"
                value="minor"
                checked={versionType === 'minor'}
                onChange={(value) => setVersionType(value as KrcVersionType)}
                readOnly={false}
              />
              <SimpleRadioButton
                name="versionType"
                label="Major versie (grote wijzigingen)"
                value="major"
                checked={versionType === 'major'}
                onChange={(value) => setVersionType(value as KrcVersionType)}
                readOnly={false}
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <div className="row">
            <div className="col-md-3">
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlBE}>
                <label className="control-label">Startdatum</label>
                <DatePicker
                  format="dd/MM/yyyy"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={datePickerDefaultOptions}
                />
                {isStartDateNotAfterLatest && latestStartDate && (
                  <small className="label-error">
                    De startdatum moet na de vorige versie (
                    {latestStartDate.toLocaleDateString('nl-BE')}) liggen.
                  </small>
                )}
              </LocalizationProvider>
            </div>
            <div className="col-md-9">
              <label className="control-label" htmlFor="snapshot-info-url">
                Wijzigingen link
              </label>
              <input
                id="snapshot-info-url"
                type="text"
                className="form-control"
                value={infoUrl}
                onChange={(event) => setInfoUrl(event.target.value)}
                style={{ padding: '8px 12px', marginTop: '1px' }}
              />
              {infoUrl && isInfoUrlInvalid && (
                <small className="label-error">De link moet beginnen met https:// of http://</small>
              )}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="control-label">Wijzigingslogboek</label>
          {isPreparingChangelog ? (
            <div
              className="well text-center"
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                height: '220px',
                justifyContent: 'center',
              }}
            >
              <div className="spinner-icon" style={{ margin: '0 0 12px', position: 'static' }} />
              <span>Wijzigingslogboek wordt voorbereid...</span>
            </div>
          ) : (
            <>
              {hasChangelogPreparationFailed && (
                <div className="alert alert-danger">
                  Het wijzigingslogboek kon niet automatisch worden voorbereid.
                </div>
              )}
              <AsideEditor
                initial={
                  mode === 'new' && changelogStatus === 'succeeded'
                    ? preparedChangelog
                    : initialChangelog
                }
                field="text"
                onTextChange={(text: string) => setChangelog(text)}
                plainText={false}
                inline={false}
                showCharacterCount={false}
                height={220}
              />
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-default" onClick={onClose}>
          Annuleren
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          {mode === 'new' ? 'Nieuwe versie publiceren' : 'Versie aanpassen'}
        </button>
      </Modal.Footer>
    </div>
  );
};

export default KrcSnapshotModal;
